import { useState } from 'react';
import { ConsentSubmission, CookiesPreferenceContentPayload } from '../../components/Compliance/CookiesPreferences';
import { xTenantId } from '../../consts';
import { useUserProfileUpdateMutation } from '../../libs/graphql/baseAppAPI/mutations/__generated__/user-profile-update.mutation.generated';
import { useUserContext } from '../../providers/UserProvider';
import { storageConsent } from '../../utils/storage';
import { useSnackbar } from '../snackbar';
import { updateGTMConsent } from '../../utils/gtmConsent';

type UseCookiesClaimsReturnValue = {
  loading: boolean;
  /**
   * Fetch cookies and set constants to state to determine rather cookies should be created or updated
   */
  fetchCookies: () => Promise<void>;
  /**
   * Incoming data for claims from platform
   */
  preferences: null | CookiesPreferenceContentPayload;
  /**
   * Set cookie preferences
   */
  setPreferences: (payload: CookiesPreferenceContentPayload | null) => void;
  /**
   * Create or update cookie claims
   */
  writeCookies: (payload: CookiesPreferenceContentPayload) => Promise<void>;
  /**
   * Update consent storage
   */
  updateConsentStorage: (payload: CookiesPreferenceContentPayload) => void;
  /**
   * Accept all consent properties to storage
   */
  acceptAllConsent: () => void;
  /**
   * Reject all non-essential consent properties to storage
   */
  rejectAllConsent: () => void;
};

/**
 * A hook that returns flags that define user's ability to observe deposit content and deposit limit prompt
 */
export function useCookies(): UseCookiesClaimsReturnValue {
  const { addSnack } = useSnackbar();
  const { fetchUserProfile, userProfileId } = useUserContext();

  const [userProfileUpdateMutation, { loading }] = useUserProfileUpdateMutation({
    onCompleted: async (data) => {
      if (!data) return;

      await fetchUserProfile();
      addSuccessSnack({ success: true });
    },
    onError: () => {
      addSuccessSnack({ success: false });
    },
  });

  const [preferences, setPreferences] = useState<null | CookiesPreferenceContentPayload>(null);

  const fetchCookies = async (): Promise<void> => {
    const stored = storageConsent.get() as Record<string, Record<string, boolean>> | null;

    const isFunctional = Boolean(stored?.consent?.functionalCookiesAllowed);
    const isPerformance = Boolean(stored?.consent?.performanceCookiesAllowed);

    setPreferences({
      isFunctional,
      isPerformance,
    });
  };

  const onCreateConsent = (consentModel: ConsentSubmission): void => {
    storageConsent.set({
      value: consentModel,
    });
  };

  const updateConsent = (consent: ConsentSubmission): void => {
    onCreateConsent({
      consent: consent?.consent,
      playerId: consent?.playerId,
    });
  };

  const updateConsentStorage = (consent: CookiesPreferenceContentPayload): void => {
    const consentData = {
      basicCookiesAllowed: true, // Always true
      functionalCookiesAllowed: Boolean(consent.isFunctional),
      performanceCookiesAllowed: Boolean(consent.isPerformance),
    };

    updateConsent({
      playerId: userProfileId,
      consent: consentData,
    });

    // Update GTM consent mode immediately
    updateGTMConsent({
      functionalCookiesAllowed: consentData.functionalCookiesAllowed,
      performanceCookiesAllowed: consentData.performanceCookiesAllowed,
    });
  };

  const acceptAllConsent = (): void => {
    updateConsent({
      playerId: userProfileId,
      consent: {
        basicCookiesAllowed: true,
        functionalCookiesAllowed: true,
        performanceCookiesAllowed: true,
      },
    });

    // Update GTM consent mode immediately
    updateGTMConsent({
      functionalCookiesAllowed: true,
      performanceCookiesAllowed: true,
    });
  };

  const rejectAllConsent = (): void => {
    updateConsent({
      playerId: userProfileId,
      consent: {
        basicCookiesAllowed: true,
        functionalCookiesAllowed: false,
        performanceCookiesAllowed: false,
      },
    });

    // Update GTM consent mode immediately
    updateGTMConsent({
      functionalCookiesAllowed: false,
      performanceCookiesAllowed: false,
    });
  };

  const writeCookies = async (payload: CookiesPreferenceContentPayload): Promise<void> => {
    await userProfileUpdateMutation({
      variables: {
        userProfileId,
        xTenantId,
        userProfileUpdateInfo: {
          basicCookiesAllowed: true,
          functionalCookiesAllowed: payload.isFunctional,
          performanceCookiesAllowed: payload.isPerformance,
          cookiePolicyVersionAccepted: '1.0',
        },
      },
    });
    updateConsentStorage(payload);
  };

  const addSuccessSnack = ({ success }: { success: boolean }): void => {
    addSnack({
      type: success ? 'success' : 'error',
      theme: 'dark',
      message: success ? 'Your preferences have been updated.' : 'Unable to save your preferences.',
    });
  };

  return {
    loading,
    preferences,
    fetchCookies,
    setPreferences,
    writeCookies,
    updateConsentStorage,
    acceptAllConsent,
    rejectAllConsent,
  };
}
